import classnames from 'classnames/bind';
import { Modal, Skeleton } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { useToggle } from '@mantine/hooks';

import Application from 'types/application';

import { useAccount } from 'hooks/api/useAccount';
import { useAPI } from 'hooks/api/useAPI';
import { useApplications } from 'hooks/api/useApplications';
import { useCurrentApplication } from 'application/hooks/useCurrentApplication';
import { useCurrentOrganization } from 'hooks/api/useCurrentOrganization';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useGroupedApplications } from 'application/hooks/useGroupedApplications';
import { useOrganizations } from 'hooks/api/useOrganizations';

import {
  ContextMenu,
  ContextMenuAction,
  ContextMenuGroup,
  ContextMenuLabel,
  ContextMenuOption,
  ContextMenuOptionGroup,
  ContextMenuSeparator,
} from 'components/ContextMenu/ContextMenu';
import LogoIcon from 'images/lumeo-logo-icon.svg?react';
import { CreateApplication } from 'components/CreateApplication/CreateApplication';
import { Icon } from 'components/Icon/Icon';
import { Pill } from 'components/Pill/Pill';
import { Text } from 'components/Text/Text';

import styles from './ApplicationSelect.module.scss';

const c = classnames.bind(styles);

export function ApplicationSelect() {
  const navigate = useNavigate();

  const { applicationID } = useAPI();

  const { data: currentApplication, isInitialLoading: isLoadingApplication } =
    useCurrentApplication();
  const { data: currentOrganization, isInitialLoading: isLoadingOrganization } =
    useCurrentOrganization();
  const { data: currentPlan, isInitialLoading: isLoadingPlan } =
    useCurrentPlan();
  const { data: applications } = useApplications();
  const { data: organizations } = useOrganizations();
  const { data: account } = useAccount();

  const groupedApplications = useGroupedApplications();

  const [isCreating, toggleIsCreating] = useToggle();

  if (!applications || !organizations || !account) {
    return null;
  }

  function handleApplicationSelect(application: Application) {
    if (!account || application.id === currentApplication?.id) {
      return;
    }

    const roles = account?.roles
      .filter(
        ({ application_id, organization_id }) =>
          application_id === currentApplication?.id ||
          organization_id === currentApplication?.organization_id
      )
      .map(({ name }) => name)
      .join(',');

    navigate(`/applications/${application.id}`);

    if (window.FrontChat && import.meta.env.VITE_FRONT_CHAT_ID) {
      window.FrontChat('identity', {
        customFields: {
          'Application ID': application.id,
          'Organization ID': application.organization_id,
          Roles: roles,
        },
      });
    }
  }

  const isStarterPlan =
    currentPlan?.id === import.meta.env.VITE_STARTER_PLAN_ID;

  return (
    <>
      <div className={c('wrap')}>
        <ContextMenu
          trigger={
            <button className={c('trigger')} aria-label="Select workspace">
              <div className={c('workspace-picture')}>
                <Skeleton radius="xl" visible={isLoadingApplication}>
                  <LogoIcon className={c('workspace-picture-img')} />
                </Skeleton>
              </div>

              {applicationID ? (
                <div>
                  <Skeleton
                    visible={isLoadingOrganization}
                    width="50%"
                    height="12px"
                    mb="2px"
                  >
                    <Text size="small">
                      {currentOrganization?.name ?? 'Organization'}
                    </Text>
                  </Skeleton>

                  <Skeleton visible={isLoadingApplication}>
                    <Text className={c('application-name')}>
                      {currentApplication?.name ?? 'Workspace'}
                    </Text>
                  </Skeleton>
                </div>
              ) : (
                <Text className={c('application-name', 'default')}>
                  Select workspace...
                </Text>
              )}

              <div className={c('icon-wrap')}>
                <Icon
                  name="unfold-vertical"
                  className={c('indicators')}
                  size="medium"
                />
              </div>
            </button>
          }
          align="start"
        >
          <ContextMenuOptionGroup>
            {groupedApplications.map(({ id, name, applications }) => (
              <ContextMenuGroup key={id}>
                <ContextMenuLabel>{name}</ContextMenuLabel>
                {applications.map((application) => (
                  <ContextMenuOption
                    value={application.id}
                    key={application.id}
                    onClick={() => handleApplicationSelect(application)}
                  >
                    {application.name}
                  </ContextMenuOption>
                ))}
              </ContextMenuGroup>
            ))}
          </ContextMenuOptionGroup>

          <ContextMenuSeparator />

          <ContextMenuAction icon="plus" onClick={toggleIsCreating}>
            Add new workspace
          </ContextMenuAction>
        </ContextMenu>

        <Skeleton visible={isLoadingPlan} radius="xl">
          <Pill intent={isStarterPlan ? 'info' : 'default'}>
            {currentPlan?.name ?? 'Starter'}
          </Pill>
        </Skeleton>
      </div>

      {isCreating && (
        <Modal
          title="Add Workspace"
          opened={isCreating}
          onClose={() => toggleIsCreating(false)}
        >
          <CreateApplication
            onClose={() => toggleIsCreating(false)}
            onFinishCreating={() => toggleIsCreating(false)}
          />
        </Modal>
      )}
    </>
  );
}
